// Pure helpers that derive the visible row set from the raw records +
// current grid UX state (search / filter / sort). Used by both the
// tabular template renderer and the realtime list view.
//
// Records here are intentionally typed loose — the helpers accept any
// shape with stringly indexable fields. Caller is expected to pass the
// embed-records' header objects directly.

import type { FilterSpec, SortSpec } from './gridTypes'

type AnyRow = Record<string, unknown>

function asNumber(v: unknown): number | null {
  if (v == null || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string') {
    const n = Number(v.trim())
    return Number.isFinite(n) ? n : null
  }
  return null
}

function asDate(v: unknown): number | null {
  if (v == null || v === '') return null
  if (v instanceof Date) {
    const t = v.getTime()
    return Number.isFinite(t) ? t : null
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const t = new Date(v).getTime()
    return Number.isFinite(t) ? t : null
  }
  return null
}

function asLowerString(v: unknown): string {
  if (v == null) return ''
  return String(v).toLowerCase()
}

export function matchesSearch(row: AnyRow, search: string): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true
  for (const key of Object.keys(row)) {
    if (key.startsWith('_')) continue
    const cell = row[key]
    if (cell == null) continue
    if (asLowerString(cell).includes(q)) return true
  }
  return false
}

export function matchesFilter(row: AnyRow, f: FilterSpec): boolean {
  const raw = row[f.column]
  // is_empty / is_not_empty operate before any kind-specific coercion.
  if (f.op === 'is_empty') return raw == null || raw === ''
  if (f.op === 'is_not_empty') return !(raw == null || raw === '')
  if (raw == null || raw === '') return false

  if (f.kind === 'number') {
    const n = asNumber(raw)
    if (n === null) return false
    const v = asNumber(f.value)
    switch (f.op) {
      case 'equals': return v !== null && n === v
      case 'not_equals': return v !== null && n !== v
      case 'gt': return v !== null && n > v
      case 'gte': return v !== null && n >= v
      case 'lt': return v !== null && n < v
      case 'lte': return v !== null && n <= v
      case 'between': {
        const from = asNumber(f.from)
        const to = asNumber(f.to)
        if (from === null && to === null) return true
        if (from !== null && n < from) return false
        if (to !== null && n > to) return false
        return true
      }
      default: return false
    }
  }

  if (f.kind === 'date') {
    const t = asDate(raw)
    if (t === null) return false
    const v = asDate(f.value)
    switch (f.op) {
      case 'equals': return v !== null && t === v
      case 'not_equals': return v !== null && t !== v
      case 'gt': return v !== null && t > v
      case 'gte': return v !== null && t >= v
      case 'lt': return v !== null && t < v
      case 'lte': return v !== null && t <= v
      case 'between': {
        const from = asDate(f.from)
        const to = asDate(f.to)
        if (from !== null && t < from) return false
        if (to !== null && t > to) return false
        return true
      }
      default: return false
    }
  }

  if (f.kind === 'enum') {
    const s = String(raw)
    if (f.op === 'in') {
      if (!Array.isArray(f.value)) return false
      return (f.value as unknown[]).map(String).includes(s)
    }
    if (f.op === 'equals') return s === String(f.value)
    if (f.op === 'not_equals') return s !== String(f.value)
    return false
  }

  // text (default)
  const hay = asLowerString(raw)
  const needle = asLowerString(f.value)
  switch (f.op) {
    case 'contains': return needle ? hay.includes(needle) : true
    case 'equals': return hay === needle
    case 'not_equals': return hay !== needle
    case 'starts_with': return hay.startsWith(needle)
    case 'ends_with': return hay.endsWith(needle)
    default: return false
  }
}

function compareCells(a: unknown, b: unknown): number {
  // Both nullish → equal. One nullish → push nulls to the end (under
  // 'asc'; the outer sort flips for desc).
  const aNull = a == null || a === ''
  const bNull = b == null || b === ''
  if (aNull && bNull) return 0
  if (aNull) return 1
  if (bNull) return -1

  const an = asNumber(a)
  const bn = asNumber(b)
  if (an !== null && bn !== null) return an - bn

  const at = asDate(a)
  const bt = asDate(b)
  if (at !== null && bt !== null) return at - bt

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

// Apply a multi-column sort. Stable for ties (relies on the input
// already being in insertion order; we don't re-key here).
export function sortRows<T extends AnyRow>(rows: T[], sort: SortSpec[]): T[] {
  if (!sort || sort.length === 0) return rows
  const decorated = rows.map((row, idx) => ({ row, idx }))
  decorated.sort((a, b) => {
    for (const s of sort) {
      const cmp = compareCells(a.row[s.column], b.row[s.column])
      if (cmp !== 0) return s.dir === 'asc' ? cmp : -cmp
    }
    return a.idx - b.idx
  })
  return decorated.map(d => d.row)
}

export function applyGridState<T extends AnyRow>(
  rows: T[],
  search: string,
  filters: FilterSpec[],
  sort: SortSpec[],
): T[] {
  let out = rows
  if (filters && filters.length > 0) {
    out = out.filter(r => filters.every(f => matchesFilter(r, f)))
  }
  if (search && search.trim()) {
    out = out.filter(r => matchesSearch(r, search))
  }
  if (sort && sort.length > 0) {
    out = sortRows(out, sort)
  }
  return out
}
